import { useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ArrowLeft, UserPlus, CheckCircle, Users, Wrench, ShieldCheck, Copy } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const InviteTeam = ({ onBack, user }) => {
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteMessage, setInviteMessage] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [showTypeDialog, setShowTypeDialog] = useState(false);
  const [inviteLink, setInviteLink] = useState('');
  const canInviteSecurest = user?.role === 'admin' || user?.role === 'zkteco_admin';

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return { Authorization: `Bearer ${token}` };
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    
    if (!inviteEmail) {
      setError("Please enter an email address");
      return;
    }

    // Show the type selection dialog
    setShowTypeDialog(true);
  };

  const handleSendInvite = async (inviteType) => {
    setShowTypeDialog(false);
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const r = await axios.post(
        `${API}/invite`,
        {
          email: inviteEmail,
          message: inviteMessage || null,
          invite_type: inviteType // 'teammate', 'installer' or 'securest'
        },
        { headers: getAuthHeaders() }
      );

      setSuccess(`${inviteType === 'securest' ? 'Securest-only invitation' : 'Invitation'} sent to ${inviteEmail}!`);
      setInviteLink(inviteType === 'securest' ? r.data?.invite_url || '' : '');
      setInviteEmail('');
      setInviteMessage('');
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to send invitation");
      setTimeout(() => setError(''), 5000);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-8">
      <div className="max-w-2xl mx-auto">
        <Button
          variant="ghost"
          onClick={onBack}
          className="mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Dashboard
        </Button>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <UserPlus className="w-6 h-6 text-green-600" />
              Invite Team/Installers
            </CardTitle>
            <CardDescription>
              Send an invitation to join the ZKTeco Solution Configurator
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleFormSubmit} className="space-y-6">
              {/* Alerts */}
              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
              {success && (
                <Alert className="bg-green-50 text-green-900 border-green-200">
                  <CheckCircle className="h-4 w-4" />
                  <AlertDescription>{success}</AlertDescription>
                </Alert>
              )}

              {inviteLink && (
                <div className="bg-teal-50 border border-teal-200 rounded-lg p-4 space-y-2" data-testid="securest-invite-link-box">
                  <p className="text-sm text-teal-900 font-medium">Securest-only invite link (single use, tied to that email):</p>
                  <div className="flex gap-2">
                    <Input readOnly value={inviteLink} className="text-xs" data-testid="securest-invite-link" />
                    <Button type="button" variant="outline" data-testid="securest-invite-copy"
                      onClick={() => { navigator.clipboard?.writeText(inviteLink); setSuccess('Link copied'); setTimeout(() => setSuccess(''), 3000); }}>
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}

              {/* Email Input */}
              <div className="space-y-2">
                <Label htmlFor="invite-email">Email Address <span className="text-red-500">*</span></Label>
                <Input
                  id="invite-email"
                  type="email"
                  placeholder="colleague@company.com"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  required
                />
              </div>

              {/* Message Input */}
              <div className="space-y-2">
                <Label htmlFor="invite-message">Personal Message (Optional)</Label>
                <Input
                  id="invite-message"
                  placeholder="Let's work together on projects..."
                  value={inviteMessage}
                  onChange={(e) => setInviteMessage(e.target.value)}
                />
              </div>

              {/* Info Box */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-900">
                  The recipient will receive an email with a registration link. They'll need to complete 
                  registration and wait for approval before accessing the configurator.
                </p>
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-green-600 hover:bg-green-700"
              >
                {loading ? 'Sending...' : 'Send Invitation'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>

      {/* Invite Type Selection Dialog */}
      <Dialog open={showTypeDialog} onOpenChange={setShowTypeDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Who are you inviting?</DialogTitle>
            <DialogDescription>
              Please select the type of access for this person.
            </DialogDescription>
          </DialogHeader>
          <div className={`grid ${canInviteSecurest ? 'grid-cols-3' : 'grid-cols-2'} gap-4 py-4`}>
            <button
              data-testid="invite-type-teammate"
              onClick={() => handleSendInvite('teammate')}
              className="flex flex-col items-center justify-center p-6 border-2 border-slate-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-all"
            >
              <Users className="w-10 h-10 text-blue-600 mb-3" />
              <span className="font-semibold text-slate-800">Teammate</span>
              <span className="text-xs text-slate-500 mt-1 text-center">Someone in your organisation</span>
            </button>
            <button
              data-testid="invite-type-installer"
              onClick={() => handleSendInvite('installer')}
              className="flex flex-col items-center justify-center p-6 border-2 border-slate-200 rounded-lg hover:border-green-500 hover:bg-green-50 transition-all"
            >
              <Wrench className="w-10 h-10 text-green-600 mb-3" />
              <span className="font-semibold text-slate-800">Installer</span>
              <span className="text-xs text-slate-500 mt-1 text-center">An external installer partner</span>
            </button>
            {canInviteSecurest && (
              <button
                data-testid="invite-type-securest"
                onClick={() => handleSendInvite('securest')}
                className="flex flex-col items-center justify-center p-6 border-2 border-slate-200 rounded-lg hover:border-teal-500 hover:bg-teal-50 transition-all"
              >
                <ShieldCheck className="w-10 h-10 text-teal-600 mb-3" />
                <span className="font-semibold text-slate-800">Securest only</span>
                <span className="text-xs text-slate-500 mt-1 text-center">Access to /securest, no main configurator</span>
              </button>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InviteTeam;
